import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, Observable, shareReplay, tap } from 'rxjs';
import { GATEWAY_URL } from './api.config';
import { SIGNED_IN_ELSEWHERE_STATE } from './auth-session';
import { browserId } from './browser-id';
import {
  AuthenticatedResponse,
  EmailCodePurpose,
  EmailCodeToken,
  PublicAdmin,
  PublicInvited,
  PublicRole,
  SignInResponse,
  TokenPair,
  TwoFactorSetup,
} from './models';
import { SESSION_SOCKET_FACTORY, SessionSocket } from './session-socket';

const TOKENS_KEY = 'auth_m_tokens';
const USER_KEY = 'auth_m_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** name -> label, filled from whichever role list has been fetched. */
  private static readonly roleLabels = new Map<string, string>();

  readonly user = signal<PublicAdmin | null>(readJson<PublicAdmin>(USER_KEY));

  /** A sign-in from another browser or device ends the session at once. */
  private readonly socket: SessionSocket;

  constructor(private http: HttpClient, private router: Router, zone: NgZone) {
    this.socket = new SessionSocket(zone, inject(SESSION_SOCKET_FACTORY),
      () => this.tokens?.access_token, () => this.refresh(), () => this.signedInElsewhere());
    // A session restored from storage (a page reload). Deferred like the other
    // portals: the socket's token refresh passes through the interceptor.
    if (this.isLoggedIn) {
      queueMicrotask(() => this.socket.connect());
    }
  }

  // ── login flow ────────────────────────────────────────────────────────

  signIn(email: string, password: string): Observable<SignInResponse> {
    // browser_id: another tab of this browser keeps the session; anywhere else
    // asks first (status 'session_conflict'), then replaces it.
    return this.http
      .post<SignInResponse>(`${GATEWAY_URL}/auth/admin/signin`, {
        email,
        password,
        browser_id: browserId(),
      })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  verifyPattern(email: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/pattern/verify`, { email, pattern });
  }

  verify2fa(challengeToken: string, code: string): Observable<AuthenticatedResponse> {
    return this.http
      .post<AuthenticatedResponse>(`${GATEWAY_URL}/auth/admin/2fa/verify`, {
        challenge_token: challengeToken,
        code,
      })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  /** The user agreed to sign out their other browser or device: finish signing in. */
  confirmSignIn(confirmToken: string): Observable<AuthenticatedResponse> {
    return this.http
      .post<AuthenticatedResponse>(`${GATEWAY_URL}/auth/admin/signin/confirm`, {
        confirm_token: confirmToken,
      })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  resend2fa(challengeToken: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/2fa/resend`, {
      challenge_token: challengeToken,
    });
  }

  // ── passwords & activation ────────────────────────────────────────────

  forgotPassword(email: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/forgot-password`, { email });
  }

  /** Exchanges an emailed code for the token of its flow (models.ts EmailCodePurpose). */
  verifyEmailCode(email: string, purpose: EmailCodePurpose, code: string): Observable<EmailCodeToken> {
    return this.http.post<EmailCodeToken>(`${GATEWAY_URL}/auth/admin/email-code/verify`, { email, purpose, code });
  }

  // Reset-password now verifies the registered phone first: send code →
  // verify (→ proof) → reset with the proof.
  resetPasswordSendCode(token: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/reset-password/send-phone-code`, { token });
  }

  resetPasswordVerifyPhone(token: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${GATEWAY_URL}/auth/admin/reset-password/verify-phone`, { token, code });
  }

  resetPassword(proofToken: string, newPassword: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/reset-password`, {
      proof_token: proofToken,
      new_password: newPassword,
    });
  }

  // Admin set-password now requires verifying the phone registered at
  // creation (the code auto-goes to that phone — no phone entry here).
  sendSetPasswordPhoneCode(token: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/set-password/send-phone-code`, { token });
  }

  verifySetPasswordPhone(token: string, code: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/set-password/verify-phone`, { token, code });
  }

  setPassword(token: string, password: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/set-password`, { token, password });
  }

  // Final onboarding step: set the mandatory pattern → activates the account.
  setOnboardingPattern(token: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/set-password/pattern`, { token, pattern });
  }

  checkSetPasswordToken(token: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${GATEWAY_URL}/auth/admin/set-password/check`, { token });
  }

  resendSetPassword(token: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/set-password/resend`, { token });
  }

  // ── forgot security pattern ───────────────────────────────────────────
  forgotPattern(email: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/forgot-pattern`, { email });
  }

  resetPatternSendCode(token: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/reset-pattern/send-phone-code`, { token });
  }

  resetPatternVerifyPhone(token: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${GATEWAY_URL}/auth/admin/reset-pattern/verify-phone`, { token, code });
  }

  resetPattern(proofToken: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/reset-pattern`, { proof_token: proofToken, pattern });
  }

  verifyCreation(token: string): Observable<PublicAdmin> {
    return this.http.get<PublicAdmin>(`${GATEWAY_URL}/auth/admin/verify-creation/${token}`);
  }

  changePassword(currentPassword: string, newPassword: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // ── admin management ──────────────────────────────────────────────────

  me(): Observable<PublicAdmin> {
    return this.http
      .get<PublicAdmin>(`${GATEWAY_URL}/auth/admin/me`)
      .pipe(tap((user) => this.storeUser(user)));
  }

  /** One admin by id. 404s for an unknown id (and for the super_admin). */
  getUser(id: string): Observable<PublicAdmin> {
    return this.http.get<PublicAdmin>(`${GATEWAY_URL}/auth/admin/users/${id}`);
  }

  // `/users` is the data-grid endpoint (paginated {data, count}); `/users/all`
  // returns every admin in one go.
  listUsers(): Observable<{ users: PublicAdmin[] }> {
    return this.http.get<{ users: PublicAdmin[] }>(`${GATEWAY_URL}/auth/admin/users/all`);
  }

  listRoles(): Observable<{ roles: PublicRole[] }> {
    return this.http
      .get<{ roles: PublicRole[] }>(`${GATEWAY_URL}/auth/admin/roles`)
      .pipe(tap((res) => this.cacheRoleLabels(res.roles)));
  }

  // Create a DYNAMIC admin role (its privileges are then configured in the
  // User Roles screen; only super_admin / assistant_admin may create).
  createRole(name: string, description: string): Observable<PublicRole> {
    return this.http
      .post<PublicRole>(`${GATEWAY_URL}/auth/admin/roles`, { name, description })
      .pipe(tap((role) => this.cacheRoleLabels([role])));
  }

  createUser(input: {
    email: string;
    /** Omit for a test account — the server names it from its role's sequence. */
    first_name?: string;
    last_name?: string;
    phone: string;
    role: string;
    is_supervisor: boolean;
    is_test: boolean;
  }): Observable<PublicAdmin> {
    return this.http.post<PublicAdmin>(`${GATEWAY_URL}/auth/admin/users`, input);
  }

  /**
   * The name the next test account in a role would get — "Test", then the
   * role's code and its next number in that role, e.g. "TEK 3". A preview only:
   * the number is taken when the account is saved.
   */
  previewTestUserName(role: string): Observable<{ first_name: string; last_name: string }> {
    return this.http.get<{ first_name: string; last_name: string }>(
      `${GATEWAY_URL}/auth/admin/users/test-name`,
      { params: { role } },
    );
  }

  /**
   * Update an admin's profile. The address is NOT part of this call — it is
   * written through setUserMetadata, which owns it.
   */
  updateUser(
    id: string,
    input: {
      email: string;
      /** Ignored for a test account, which keeps its assigned name. */
      first_name?: string;
      last_name?: string;
      phone: string;
      role?: string;
      is_supervisor?: boolean;
      is_test?: boolean;
    },
  ): Observable<PublicAdmin> {
    return this.http.put<PublicAdmin>(`${GATEWAY_URL}/auth/admin/users/${id}`, input);
  }

  unlockUser(id: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/users/${id}/unlock`, {});
  }

  /** Emails the CALLER a fresh creation-verification link for an account not yet verified. */
  resendCreationVerification(id: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/users/${id}/resend-creation-verification`, {});
  }

  /** Emails the account a fresh set-password link (creation verified, password not set yet). */
  resendUserSetPassword(id: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/users/${id}/resend-set-password`, {});
  }

  /**
   * Activate / deactivate an admin. Authority is enforced by Auth M (a
   * supervisor of the same role, or a higher role); deactivating also ends the
   * target's sessions.
   */
  setUserActive(id: string, active: boolean): Observable<unknown> {
    const action = active ? 'activate' : 'deactivate';
    return this.http.post(`${GATEWAY_URL}/auth/admin/users/${id}/${action}`, {});
  }

  /**
   * Update YOUR OWN profile. Distinct from updateUser(): that one is the
   * admin-management endpoint, guarded by authority over the target's role —
   * a rule nobody satisfies for their own account.
   *
   * Neither the email nor the phone is here: the email is the sign-in identity
   * and the phone is where security codes go, so both move only through the
   * verified flows below.
   */
  updateOwnProfile(input: {
    first_name: string;
    last_name: string;
  }): Observable<PublicAdmin> {
    return this.http.put<PublicAdmin>(`${GATEWAY_URL}/auth/admin/me`, input);
  }

  // ── changing your email address or phone number ─────────────────────
  //
  // Both take two codes, entered here on the Security page. The first proves
  // the request; the second applies the change:
  //
  //   email: code -> registered phone, then the new address -> code -> NEW address
  //   phone: code -> the NEW phone                          -> code -> REGISTERED address
  //
  // The second code is entered on the Security page and applies the change.

  /** Email, step 1: texts a code to the REGISTERED phone — before any new address is asked for. */
  startEmailChange(): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${GATEWAY_URL}/auth/admin/me/email-change`,
      {},
    );
  }

  /** Email, step 2: verify that code. The new address is only asked for after this. */
  verifyEmailChangeCode(code: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${GATEWAY_URL}/auth/admin/me/email-change/verify-code`, {
      code,
    });
  }

  /** Email, step 3: the new address — a confirmation code is emailed to it. */
  submitNewEmail(newEmail: string): Observable<{ status: string; new_email: string }> {
    return this.http.post<{ status: string; new_email: string }>(
      `${GATEWAY_URL}/auth/admin/me/email-change/new-email`,
      { new_email: newEmail },
    );
  }

  /** Phone, step 1: texts a code to the NEW number. */
  startPhoneChange(newPhone: string): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${GATEWAY_URL}/auth/admin/me/phone-change`,
      { new_phone: newPhone },
    );
  }

  /** Phone, step 2: the code checks out, so a code goes to the REGISTERED address. */
  verifyPhoneChangeCode(code: string): Observable<{ status: string; email_hint: string }> {
    return this.http.post<{ status: string; email_hint: string }>(
      `${GATEWAY_URL}/auth/admin/me/phone-change/verify-code`,
      { code },
    );
  }

  /** Email, step 4: the code from the NEW address — applies the change. */
  confirmEmailChange(code: string): Observable<{ status: string; email: string }> {
    return this.http.post<{ status: string; email: string }>(
      `${GATEWAY_URL}/auth/admin/me/email-change/confirm`,
      { code },
    );
  }

  /** Phone, step 3: the code from the REGISTERED address — applies the change. */
  confirmPhoneChange(code: string): Observable<{ status: string; phone: string }> {
    return this.http.post<{ status: string; phone: string }>(
      `${GATEWAY_URL}/auth/admin/me/phone-change/confirm`,
      { code },
    );
  }

  /** Every metadata entry for a user (e.g. the stored 'address'). */
  listUserMetadata(userId: string): Observable<{ metadata: { key: string; value: any }[] }> {
    return this.http.get<{ metadata: { key: string; value: any }[] }>(
      `${GATEWAY_URL}/auth/admin/users/${userId}/metadata`,
    );
  }

  /** Upsert one metadata key (arbitrary JSON value) for a user, e.g. 'address'. */
  setUserMetadata(userId: string, key: string, value: unknown): Observable<unknown> {
    return this.http.put(`${GATEWAY_URL}/auth/admin/users/${userId}/metadata/${key}`, value);
  }

  // ── invited users (admin manages) ─────────────────────────────────────
  listInvitedRoles(): Observable<{ roles: PublicRole[] }> {
    return this.http
      .get<{ roles: PublicRole[] }>(`${GATEWAY_URL}/auth/invited/roles`)
      .pipe(tap((res) => this.cacheRoleLabels(res.roles)));
  }

  listInvited(): Observable<{ users: PublicInvited[] }> {
    return this.http.get<{ users: PublicInvited[] }>(`${GATEWAY_URL}/auth/invited/users`);
  }

  inviteUser(input: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
  }): Observable<PublicInvited> {
    return this.http.post<PublicInvited>(`${GATEWAY_URL}/auth/invited/users`, input);
  }

  setSecurityPattern(pattern: string[]): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/security-pattern`, { pattern });
  }

  /**
   * Changing an existing pattern is phone-verified: a code goes to the
   * REGISTERED phone first. Setting a first pattern adds a factor and needs no
   * proof; replacing one weakens a gate, so holding the session is not on its
   * own enough. A pattern can't be removed.
   */
  sendPatternCode(): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${GATEWAY_URL}/auth/admin/security-pattern/send-phone-code`,
      {},
    );
  }

  /** Change, step 2: the code checks out → a short-lived proof unlocks the new pattern. */
  verifyPatternChangeCode(code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(
      `${GATEWAY_URL}/auth/admin/security-pattern/verify-phone`,
      { code },
    );
  }

  /** Change, step 3: save the new pattern with that proof. */
  changeSecurityPattern(proofToken: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/security-pattern/change`, {
      proof_token: proofToken,
      pattern,
    });
  }

  // ── 2FA method management ─────────────────────────────────────────────
  setupAuthenticator(currentPassword: string): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>(`${GATEWAY_URL}/auth/admin/2fa/setup`, {
      current_password: currentPassword,
    });
  }

  confirmAuthenticator(code: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/2fa/confirm`, { code });
  }

  removeAuthenticator(currentPassword: string): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/2fa/remove`, { current_password: currentPassword });
  }

  // Set the default (preselected) login channel — no password required.
  setDefaultMethod(method: 'email' | 'sms' | 'totp'): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/2fa/method`, { method });
  }

  // Login-time channel picker (uses the pre-auth challenge, not a session).
  select2fa(challengeToken: string, method: 'email' | 'sms' | 'totp'): Observable<unknown> {
    return this.http.post(`${GATEWAY_URL}/auth/admin/2fa/select`, {
      challenge_token: challengeToken,
      method,
    });
  }

  // ── session ───────────────────────────────────────────────────────────

  get tokens(): TokenPair | null {
    return readJson<TokenPair>(TOKENS_KEY);
  }

  get isLoggedIn(): boolean {
    return !!this.tokens;
  }

  // Single-flight: refresh tokens are single-use (rotated on the server), so
  // concurrent 401s must share ONE refresh — otherwise the second call sends
  // an already-rotated token, gets a 401, and (previously) wiped the whole
  // session. The in-flight observable is cached until it settles.
  private refreshInFlight$: Observable<TokenPair> | null = null;

  refresh(): Observable<TokenPair> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    const refreshToken = this.tokens?.refresh_token ?? '';
    this.refreshInFlight$ = this.http
      .post<TokenPair>(`${GATEWAY_URL}/auth/refresh`, { refresh_token: refreshToken })
      .pipe(
        tap((pair) => sessionStorage.setItem(TOKENS_KEY, JSON.stringify(pair))),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => (this.refreshInFlight$ = null)),
      );
    return this.refreshInFlight$;
  }

  logout(): void {
    const refreshToken = this.tokens?.refresh_token;
    if (refreshToken) {
      this.http.post(`${GATEWAY_URL}/auth/logout`, { refresh_token: refreshToken }).subscribe({
        error: () => undefined,
      });
    }
    this.clearSession();
    this.router.navigate(['/x/login']);
  }

  clearSession(): void {
    this.socket.disconnect();
    sessionStorage.removeItem(TOKENS_KEY);
    sessionStorage.removeItem(USER_KEY);
    this.user.set(null);
  }

  /**
   * A sign-in from another browser or device replaced this session (heard on
   * the socket, or from a 401 "session_revoked"): end it here and say why.
   */
  signedInElsewhere(): void {
    if (!this.isLoggedIn) return;
    this.clearSession();
    this.router.navigate(['/x/login'], { state: SIGNED_IN_ELSEWHERE_STATE });
  }

  /** A session conflict (or a pending 2FA step) carries no tokens yet. */
  private storeIfSignedIn(res: { status: string; user?: PublicAdmin; tokens?: TokenPair }): void {
    if (res.status === 'ok' && res.user && res.tokens) {
      this.storeSession(res.user, res.tokens);
    }
  }

  private storeSession(user: PublicAdmin, tokens: TokenPair): void {
    sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    this.storeUser(user);
    this.socket.connect();
  }

  private storeUser(user: PublicAdmin): void {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    this.user.set(user);
  }

  /**
   * Display name for a role. Labels come from the DB (cached whenever a role
   * list is fetched); the fallback derives one from the slug so a role this
   * session has not seen still renders readably instead of as "content_reviewer".
   */
  getRoleLabel(role: string): string {
    if (!role) return '';
    const known = AuthService.roleLabels.get(role);
    if (known) return known;
    return role
      .split('_')
      .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  }

  private cacheRoleLabels(roles: PublicRole[] | undefined): void {
    for (const r of roles ?? []) {
      if (r?.name && r?.label) AuthService.roleLabels.set(r.name, r.label);
    }
  }
}

/** Segments that stay upper-case when a label is derived from a slug. */
const ACRONYMS = new Set(['cms', 'qa', 'hr', 'it', 'api']);

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
